import React, { useState, useEffect, useRef } from 'react';
import { Mic, MicOff, Phone, RotateCcw } from 'lucide-react';

const LandingPageDemo = () => {
  const [status, setStatus] = useState('idle'); // idle, connecting, ready, listening, processing, speaking, done
  const [transcript, setTranscript] = useState([]);
  const [callDuration, setCallDuration] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [wsConnection, setWsConnection] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [sessionId, setSessionId] = useState(null);

  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const streamRef = useRef(null);
  const durationIntervalRef = useRef(null);
  const transcriptEndRef = useRef(null);

  // Initialize audio context and get microphone access
  const initializeAudio = async () => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }

      if (!streamRef.current) {
        streamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
      }

      return streamRef.current;
    } catch (error) {
      console.error('Audio initialization error:', error);
      setErrorMessage('Microphone access denied. Please enable microphone permissions.');
      return null;
    }
  };

  // Start demo call
  const startDemo = async () => {
    try {
      setErrorMessage('');
      setStatus('connecting');
      setTranscript([]);
      setCallDuration(0);
      setIsRecording(false);

      // Get microphone access
      const stream = await initializeAudio();
      if (!stream) {
        setStatus('idle');
        return;
      }

      // Request WebSocket connection from backend
      const response = await fetch('/api/demo/start-call', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        throw new Error('Failed to start demo call');
      }

      const data = await response.json();
      const sid = data.sessionId;
      setSessionId(sid);

      // Create WebSocket URL
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${window.location.host}/demo-voice-agent?sessionId=${sid}`;

      // Connect to WebSocket
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('[Demo] WebSocket connected');
        setWsConnection(ws);
        setStatus('ready');

        // Start call timer
        durationIntervalRef.current = setInterval(() => {
          setCallDuration((d) => {
            if (d >= 60) {
              // Auto-end call after 60 seconds
              endDemo(ws);
              return 60;
            }
            return d + 1;
          });
        }, 1000);
      };

      ws.onmessage = async (event) => {
        try {
          const message = JSON.parse(event.data);

          if (message.type === 'ready') {
            console.log('[Demo] Ready message received');
            setStatus('listening');
          } else if (message.type === 'transcript') {
            // Add to transcript
            console.log(`[Demo] Transcript: ${message.role} - ${message.text}`);
            setTranscript((prev) => [...prev, { role: message.role, text: message.text }]);
          } else if (message.type === 'audio') {
            // Play audio response
            console.log(`[Demo] Audio message received (${message.audio?.length} chars)`);
            setStatus('speaking');
            await playAudio(message.audio, message.format || 'mp3');
          } else if (message.type === 'status') {
            console.log(`[Demo] Status: ${message.state}`);
            setStatus(message.state);
          } else if (message.type === 'metrics') {
            // Update metrics if needed
          }
        } catch (error) {
          console.error('[Demo] Message parse error:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setErrorMessage('Connection error. Please try again.');
        setStatus('idle');
      };

      ws.onclose = () => {
        console.log('[Demo] WebSocket closed');
        setWsConnection(null);
        if (status !== 'done') {
          setStatus('idle');
        }
        clearInterval(durationIntervalRef.current);
      };
    } catch (error) {
      console.error('Demo start error:', error);
      setErrorMessage(error.message || 'Failed to start demo. Please try again.');
      setStatus('idle');
    }
  };

  // Toggle recording
  const toggleRecording = async () => {
    try {
      if (isRecording) {
        // Stop recording
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
          mediaRecorderRef.current.stop();
          setIsRecording(false);

          // Send stop message to server
          if (wsConnection && wsConnection.readyState === WebSocket.OPEN) {
            wsConnection.send(JSON.stringify({ type: 'stop_recording' }));
          }
        }
      } else {
        // Start recording
        const stream = await initializeAudio();
        if (!stream) {
          return;
        }

        const mediaRecorder = new MediaRecorder(stream);
        mediaRecorderRef.current = mediaRecorder;

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            // Send audio to server
            const reader = new FileReader();
            reader.onload = () => {
              const arrayBuffer = reader.result;
              const base64Audio = btoa(
                String.fromCharCode(...new Uint8Array(arrayBuffer))
              );

              if (wsConnection && wsConnection.readyState === WebSocket.OPEN) {
                wsConnection.send(
                  JSON.stringify({
                    type: 'audio',
                    audio: base64Audio,
                    format: 'wav'
                  })
                );
              }
            };
            reader.readAsArrayBuffer(event.data);
          }
        };

        mediaRecorder.start();
        setIsRecording(true);
        setStatus('listening');
      }
    } catch (error) {
      console.error('Recording error:', error);
      setErrorMessage('Microphone error. Please check permissions.');
    }
  };

  // Play audio response and wait for completion
  const playAudio = async (audioBase64, format) => {
    return new Promise((resolve) => {
      try {
        if (!audioBase64) {
          console.warn('[Demo] No audio data to play');
          resolve();
          return;
        }

        // Decode base64 to binary
        const binaryString = atob(audioBase64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        // Initialize audio context if needed
        if (!audioContextRef.current) {
          audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }

        const audioContext = audioContextRef.current;

        // Resume audio context if suspended (required for autoplay)
        if (audioContext.state === 'suspended') {
          audioContext.resume();
        }

        // Try Web Audio API first (for WAV)
        try {
          audioContext.decodeAudioData(bytes.buffer, (decodedAudio) => {
            const source = audioContext.createBufferSource();
            source.buffer = decodedAudio;
            source.connect(audioContext.destination);
            source.onended = () => resolve();
            source.start(0);
          });
        } catch (decodeError) {
          console.log('[Demo] Using HTML5 Audio fallback for MP3 playback');
          // Fallback: Use HTML5 Audio element for MP3 playback
          const blob = new Blob([bytes], { type: 'audio/mpeg' });
          const audioUrl = URL.createObjectURL(blob);
          const audioElement = new Audio();
          audioElement.onended = () => {
            URL.revokeObjectURL(audioUrl);
            resolve();
          };
          audioElement.onerror = () => {
            console.error('[Demo] Audio playback failed');
            URL.revokeObjectURL(audioUrl);
            resolve();
          };
          audioElement.src = audioUrl;
          audioElement.play().catch((err) => {
            console.error('[Demo] Play error:', err);
            resolve();
          });
        }
      } catch (error) {
        console.error('[Demo] Audio playback error:', error);
        resolve(); // Resolve even on error so conversation continues
      }
    });
  };

  // End demo
  const endDemo = (ws = wsConnection) => {
    try {
      setStatus('done');
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      setIsRecording(false);
      clearInterval(durationIntervalRef.current);

      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
      setWsConnection(null);
    } catch (error) {
      console.error('End demo error:', error);
    }
  };

  // Reset demo
  const resetDemo = () => {
    endDemo();
    setTranscript([]);
    setCallDuration(0);
    setStatus('idle');
    setErrorMessage('');
    setSessionId(null);
  };

  // Auto-scroll transcript
  useEffect(() => {
    if (transcriptEndRef.current) {
      transcriptEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [transcript]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current) {
        try {
          if (mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.stop();
          }
        } catch (e) {
          // Already stopped
        }
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      clearInterval(durationIntervalRef.current);
    };
  }, []);

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusColor = () => {
    switch (status) {
      case 'listening':
        return 'bg-cyan-500';
      case 'processing':
        return 'bg-amber-500';
      case 'speaking':
        return 'bg-blue-500';
      case 'done':
        return 'bg-green-500';
      default:
        return 'bg-gray-400';
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'idle':
        return 'Ready to start';
      case 'connecting':
        return 'Connecting...';
      case 'ready':
        return 'Ready to listen';
      case 'listening':
        return 'Listening...';
      case 'processing':
        return 'Processing...';
      case 'speaking':
        return 'Agent speaking...';
      case 'done':
        return 'Call ended';
      default:
        return 'Unknown';
    }
  };

  return (
    <div className="landing-demo-container">
      <div className="landing-demo-card">
        {/* Header */}
        <div className="landing-demo-header">
          <div className="landing-demo-agent-info">
            <div className="landing-demo-avatar">
              <div className={`landing-demo-avatar-circle ${getStatusColor()}`}></div>
            </div>
            <div>
              <h3 className="landing-demo-agent-name">Rachel</h3>
              <p className="landing-demo-agent-title">AI Receptionist Demo</p>
            </div>
          </div>
          <div className="landing-demo-timer">{formatDuration(callDuration)}</div>
        </div>

        {/* Status Indicator */}
        <div className="landing-demo-status">
          <div className={`landing-demo-status-dot ${getStatusColor()}`}></div>
          <span className="landing-demo-status-text">{getStatusText()}</span>
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div className="landing-demo-error">
            <p>{errorMessage}</p>
          </div>
        )}

        {/* Transcript */}
        <div className="landing-demo-transcript">
          {transcript.length === 0 && status === 'idle' ? (
            <div className="landing-demo-placeholder">
              <p>Click "Start Demo" to begin a conversation with our AI receptionist.</p>
              <p style={{ fontSize: '0.875rem', marginTop: '0.5rem', opacity: 0.7 }}>
                You'll be able to speak naturally, ask questions, and even schedule an appointment.
              </p>
            </div>
          ) : (
            transcript.map((msg, idx) => (
              <div key={idx} className={`landing-demo-message landing-demo-message-${msg.role}`}>
                <div className="landing-demo-message-label">
                  {msg.role === 'agent' ? '👩 Rachel' : '👤 You'}
                </div>
                <div className="landing-demo-message-text">{msg.text}</div>
              </div>
            ))
          )}
          <div ref={transcriptEndRef} />
        </div>

        {/* Controls */}
        <div className="landing-demo-controls">
          {status === 'idle' || status === 'done' ? (
            <button className="landing-demo-btn landing-demo-btn-primary" onClick={startDemo}>
              <Phone className="w-4 h-4" />
              Start Demo
            </button>
          ) : (
            <>
              <button
                className={`landing-demo-btn landing-demo-btn-mic ${isRecording ? 'recording' : ''}`}
                onClick={toggleRecording}
                disabled={status === 'connecting'}
              >
                {isRecording ? (
                  <MicOff className="w-4 h-4" />
                ) : (
                  <Mic className="w-4 h-4" />
                )}
                {isRecording ? 'Stop' : 'Speak'}
              </button>
              <button className="landing-demo-btn landing-demo-btn-end" onClick={() => endDemo()}>
                <Phone className="w-4 h-4" style={{ transform: 'rotate(135deg)' }} />
                End Call
              </button>
            </>
          )}

          {(status === 'done' || (status !== 'idle' && status !== 'connecting')) && (
            <button className="landing-demo-btn landing-demo-btn-secondary" onClick={resetDemo}>
              <RotateCcw className="w-4 h-4" />
              Try Again
            </button>
          )}
        </div>

        {/* Footer */}
        <div className="landing-demo-footer">
          <p>This is a live demo. Your conversation is not recorded or stored.</p>
        </div>
      </div>
    </div>
  );
};

export default LandingPageDemo;
